import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { KeyRound, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ChangePasswordDialog(props) {
  const token = props.token;
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setCurrent('');
    setNext('');
    setConfirm('');
    setShowPw(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!current || !next) {
      toast.error('Попълни текущата и новата парола');
      return;
    }
    if (next.length < 6) {
      toast.error('Новата парола трябва да е поне 6 символа');
      return;
    }
    if (next !== confirm) {
      toast.error('Двете нови пароли не съвпадат');
      return;
    }
    if (next === current) {
      toast.error('Новата парола е същата като текущата');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        API_URL + '/auth/change-password',
        { current_password: current, new_password: next },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      toast.success('Паролата е сменена успешно');
      reset();
      setOpen(false);
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      toast.error(detail || 'Грешка при смяна на паролата');
    } finally {
      setSubmitting(false);
    }
  }

  function handleOpenChange(v) {
    setOpen(v);
    if (!v) reset();
  }

  return (
    <React.Fragment>
      <Button
        variant="outline"
        size="sm"
        data-testid="open-change-password-btn"
        className="flex items-center gap-2"
        onClick={function () { setOpen(true); }}
      >
        <KeyRound className="h-4 w-4" />
        <span className="hidden sm:inline">Смяна на парола</span>
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent data-testid="change-password-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[#00966E]" />
              Смяна на парола
            </DialogTitle>
            <DialogDescription>
              Въведи текущата си парола и избери нова (минимум 6 символа).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="cp-current">Текуща парола</Label>
              <Input
                id="cp-current"
                data-testid="current-password-input"
                type={showPw ? 'text' : 'password'}
                value={current}
                onChange={function (e) { setCurrent(e.target.value); }}
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label htmlFor="cp-new">Нова парола</Label>
              <Input
                id="cp-new"
                data-testid="new-password-input"
                type={showPw ? 'text' : 'password'}
                value={next}
                onChange={function (e) { setNext(e.target.value); }}
                autoComplete="new-password"
                minLength={6}
              />
            </div>
            <div>
              <Label htmlFor="cp-confirm">Потвърди новата парола</Label>
              <Input
                id="cp-confirm"
                data-testid="confirm-password-input"
                type={showPw ? 'text' : 'password'}
                value={confirm}
                onChange={function (e) { setConfirm(e.target.value); }}
                autoComplete="new-password"
              />
            </div>
            <button
              type="button"
              onClick={function () { setShowPw(function (v) { return !v; }); }}
              className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
            >
              {showPw ? (
                <React.Fragment>
                  <EyeOff className="h-3 w-3" />
                  <span>Скрий</span>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Eye className="h-3 w-3" />
                  <span>Покажи паролите</span>
                </React.Fragment>
              )}
            </button>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={function () { setOpen(false); reset(); }}
              >
                Отказ
              </Button>
              <Button
                type="submit"
                data-testid="submit-change-password-btn"
                disabled={submitting}
                className="bg-[#00966E] hover:bg-[#007a59] text-white"
              >
                {submitting ? 'Записване...' : 'Смени паролата'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
